import { MapToolDefaults, MapChangeEvent, MapObjectForm, MapToolState, MapTool, DataChangeEvent } from 'geovisto';

/**
 * The class provides API for the selection of map elements.
 * It wraps reference to the source element and list of identifiers of geographic items.
 *
 * @author Jiri Hynek
 */
class MapSelection {
    /**
     * It creates a map selection.
     *
     * @param tool
     * @param srcIds
     */
    constructor(tool, srcIds) {
        this.tool = tool;
        this.srcIds = srcIds;
        this.allIds = srcIds.map((x) => x);
    }
    /**
     * It returns the tool of the selected element.
     */
    getTool() {
        return this.tool;
    }
    /**
     * It returns identifiers of geographical items which were selected.
     */
    getSrcIds() {
        return this.srcIds;
    }
    /**
     * It returns identifiers of geographical items which were selected or affected by this selection.
     */
    getIds() {
        return this.allIds;
    }
    /**
     * It compares two map selections.
     *
     * @param selection
     */
    equals(selection) {
        if (selection) {
            const srcIds = this.getSrcIds();
            const srcIds2 = selection.getSrcIds();
            if (this.getTool() == selection.getTool() && srcIds.length == srcIds2.length) {
                for (let i = 0; i < srcIds.length; i++) {
                    if (this.srcIds[i] != srcIds2[i]) {
                        return false;
                    }
                    return true;
                }
            }
        }
        return false;
    }
    /**
     * It takes geographical items and returns the list of new ids
     * which were added to the list of all ids.
     *
     * @param ids
     */
    addIds(ids) {
        const newIds = [];
        for (let i = 0; i < ids.length; i++) {
            if (!this.allIds.includes(ids[i])) {
                this.allIds.push(ids[i]);
                newIds.push(ids[i]);
            }
        }
        return newIds;
    }
}

// Geovisto core
/**
 * This class provide functions which return the default state values.
 *
 * @author Jiri Hynek
 */
class SelectionToolDefaults extends MapToolDefaults {
    /**
     * It returns the default config.
     */
    getConfig() {
        const config = super.getConfig();
        config.selection = undefined;
        return config;
    }
    /**
     * Only one selection tool should be present in the Geovisto map.
     */
    isSingleton() {
        return true;
    }
    /**
     * It returns a unique string of the tool type.
     */
    getType() {
        return SelectionToolDefaults.TYPE;
    }
    /**
     * It returns the label of the tool.
     */
    getLabel() {
        return "Selection";
    }
    /**
     * It returns the icon of the tool.
     */
    getIcon() {
        return '<i class="fa fa-file"></i>';
    }
    /**
     * It returns default map selection.
     */
    getSelection() {
        return null;
    }
}
/**
 * Static tool type constant.
 */
SelectionToolDefaults.TYPE = "geovisto-tool-selection";

// Geovisto core
/**
 * This class provides the map selection change event.
 *
 * @author Jiri Hynek
 */
class SelectionToolEvent extends MapChangeEvent {
    /**
     * It initializes event.
     */
    constructor(selectionTool, selection) {
        super(SelectionToolEvent.TYPE(), selectionTool, selection);
    }
    /**
     * Type of the event.
     */
    static TYPE() {
        return "selection-tool-event";
    }
}

/**
 * API for Selection Tool.
 *
 * @author Jiri Hynek
 */
const SelectionToolAPI = {
    getType: () => SelectionToolDefaults.TYPE,
    getSelection: () => null,
    getChangeEventType: () => SelectionToolEvent.TYPE(),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setSelection: (selection) => { return; },
    createSelection: (source, ids) => new MapSelection(source, ids)
};

// Geovisto core
/**
 * This class represents tab fragment for Themes tool.
 *
 * @author Jiri Hynek
 */
class SelectionToolMapForm extends MapObjectForm {
    /**
     * It creates new map form with respect to the given props.
     *
     * @param tool
     */
    constructor(tool) {
        super(tool);
    }
    /**
     * It returns a HTML div element conatining the form.
     */
    getContent() {
        if (this.htmlContent == undefined) {
            this.htmlContent = this.createContent();
        }
        return this.htmlContent;
    }
    /**
     * Help function which creates the HTML div element.
     */
    createContent() {
        // tab pane
        const htmlContent = document.createElement('div');
        // TODO ... (selection settings)
        return htmlContent;
    }
}

// Geovisto core
/**
 * This class provide functions for using selections.
 *
 * @author Jiri Hynek
 */
class SelectionToolState extends MapToolState {
    /**
     * It creates a tool state.
     */
    constructor(tool) {
        super(tool);
    }
    /**
     * It resets state with respect to initial props.
     */
    initialize(defaults, props, initProps) {
        // set selection
        this.setSelection(props.selection == undefined ? defaults.getSelection() : props.selection);
        // set super props
        super.initialize(defaults, props, initProps);
    }
    /**
     * The metod takes config and deserializes the values.
     *
     * @param config
     */
    deserialize(config) {
        var _a;
        super.deserialize(config);
        // deserialize selection
        if (config.selection) {
            const tool = (_a = this.getMap()) === null || _a === void 0 ? void 0 : _a.getState().getTools().getById(config.selection.tool);
            if (tool) {
                this.setSelection(new MapSelection(tool, config.selection.ids));
            }
        }
    }
    /**
     * The method serializes the tool state. Optionally, defaults can be set if property is undefined.
     *
     * @param defaults
     */
    serialize(defaults) {
        const config = super.serialize(defaults);
        // serialize the selection
        const selection = this.getSelection();
        if (selection && selection.getSrcIds().length > 0) {
            config.selection = {
                tool: selection.getTool().getId(),
                ids: selection.getSrcIds()
            };
        }
        return config;
    }
    /**
     * It returns the selection property of the tool state.
     */
    getSelection() {
        return this.selection;
    }
    /**
     * It sets the selection property of the tool state.
     *
     * @param selection
     */
    setSelection(selection) {
        this.selection = selection;
    }
}

// Geovisto core
/**
 * This class provides the selection tool.
 *
 * @author Jiri Hynek
 */
class SelectionTool extends MapTool {
    /**
     * It creates a new tool with respect to the props.
     *
     * @paramps
     */
    constructor(props) {
        super(props);
    }
    /**
     * It creates a copy of the uninitialized tool.
     */
    copy() {
        return new SelectionTool(this.getProps());
    }
    /**
     * It returns the props given by the programmer.
     */
    getProps() {
        return super.getProps();
    }
    /**
     * It returns default values of the selection tool.
     */
    getDefaults() {
        return super.getDefaults();
    }
    /**
     * It creates new defaults of the tool.
     */
    createDefaults() {
        return new SelectionToolDefaults();
    }
    /**
     * It returns the selection tool state.
     */
    getState() {
        return super.getState();
    }
    /**
     * It creates the tool state.
     */
    createState() {
        return new SelectionToolState(this);
    }
    /**
     * It returns the tool API
     */
    getAPIGetter() {
        return {
            getGeovistoSelectionTool: () => SelectionTool.api
        };
    }
    /**
     * Overrides the super method.
     *
     * @param initProps
     */
    initialize(initProps) {
        this.initializeAPI();
        return super.initialize(initProps);
    }
    /**
     * Help method which initializes the API.
     */
    initializeAPI() {
        // use this tool reference in API (this tool should be used as a singleton)
        SelectionTool.api.getSelection = () => this.getState().getSelection();
        SelectionTool.api.createSelection = (source, ids) => new MapSelection(source, ids);
        SelectionTool.api.setSelection = (selection) => this.setSelection(selection);
    }
    /**
     * It creates new filter tool.
     */
    create() {
        // set selection
        // not necessary
        //this.setSelection(this.getState().getSelection());
        return this;
    }
    /**
     *
     * @paramection
     */
    setSelection(selection) {
        var _a;
        // if the selection tool is enabled, update map selection
        if (this.isEnabled()) {
            // update tool state
            this.getState().setSelection(selection);
            // dispatch event
            (_a = this.getMap()) === null || _a === void 0 ? void 0 : _a.getState().getEventManager().scheduleEvent(new SelectionToolEvent(this, selection), undefined, undefined);
        }
    }
    /**
     * It returns a tab fragment.
     */
    getMapForm() {
        if (this.mapForm == undefined) {
            this.mapForm = this.createMapForm();
        }
        return this.mapForm;
    }
    /**
     * It creates new tab control.
     */
    createMapForm() {
        return new SelectionToolMapForm(this);
    }
    /**
     * This function is called when a custom event is invoked.
     *
     * @param event
     */
    handleEvent(event) {
        if (event.getType() == DataChangeEvent.TYPE()) {
            // if data has been changed reset the selection
            const selection = this.getState().getSelection();
            if (event.getAnimateOptions()) {
                if (selection) {
                    this.setSelection(null);
                }
            }
            else {
                if (selection) {
                    this.setSelection(new MapSelection(selection.getTool(), selection.getSrcIds()));
                }
            }
        }
        return;
    }
}
/**
 * tool api
 */
SelectionTool.api = SelectionToolAPI;

/**
 * Factory for Selection Tool.
 *
 * @author Jiri Hynek
 */
const GeovistoSelectionTool = Object.assign(Object.assign({}, SelectionToolAPI), { createTool: (props) => new SelectionTool(props) });

export { GeovistoSelectionTool, MapSelection, SelectionTool, SelectionToolAPI, SelectionToolDefaults, SelectionToolEvent, SelectionToolMapForm, SelectionToolState };
//# sourceMappingURL=index.es.js.map
